import "./FormField.scss";

import clsx from "clsx";
import React from "react";
import { FieldValues, RegisterOptions } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ErrorMessage } from "@/components/Interface/FormFields/ErrorMessage/ErrorMessage";
import {
  extendFormOptions,
  FormFieldProps,
  getFormFieldError,
} from "@/components/Interface/FormFields/FormField/FormField.utils.ts";
import { InputLabel } from "@/components/Interface/FormFields/InputLabel/InputLabel";
import { getNestedValue } from "@/utils/objectUtils";

export const FormField = <TFormValues extends FieldValues>({
  id,
  name,
  label,
  ariaLabel,
  options = {
    required: true,
  },
  errors,
  register,
  type = "text",
  autocomplete,
  placeholder = "",
  onFocus,
  children,
  value,
  icon,
  passwordRules,
  className,
  inputMode,
}: React.PropsWithChildren<FormFieldProps<TFormValues>>) => {
  const { t } = useTranslation();
  const optionsWithParsedMaxLength = extendFormOptions(options, t);
  const errorMessage: string = getFormFieldError(
    errors,
    name,
    optionsWithParsedMaxLength,
  );

  // Use getNestedValue to safely access nested field errors
  const fieldError = getNestedValue(errors, name);
  const hasError = fieldError !== undefined;

  // Convention: required fields have no indicator; optional fields show "(optional)"
  const isOptional =
    !options?.required && !options?.readOnly && !options?.disabled;

  const optionalLabel = t([
    "translation:Common.label.optional",
    "Onboarding.sections.personal-details.form.optionalLabel",
  ]);

  const labelWithOptional =
    label && isOptional ? `${label} ${optionalLabel}` : (label ?? "");

  return (
    <div
      className={clsx("form-field-container", className, !!icon && "withIcon")}
    >
      {label && (
        <div className="label-container">
          <InputLabel
            error={hasError}
            htmlFor={id}
            label={labelWithOptional}
            disabled={!!options?.disabled || !!options.readOnly}
          />
          {children}
        </div>
      )}

      <div className="relative">
        <input
          id={id}
          aria-label={label ?? ariaLabel}
          aria-required={!!options?.required}
          aria-invalid={hasError ? "true" : "false"}
          autoComplete={autocomplete}
          {...register(
            name,
            optionsWithParsedMaxLength as RegisterOptions<TFormValues>,
          )}
          {...(options.readOnly && { readOnly: true })}
          {...(type && { type })}
          {...(value && { value })}
          {...(passwordRules && { passwordrules: passwordRules })}
          {...(inputMode && { inputMode })}
          data-testid={name}
          placeholder={placeholder}
          {...(onFocus && { onFocus })}
          className={clsx(hasError ? "error" : "", "min-w-max")}
        />
        {icon && <div className="icon">{icon}</div>}
      </div>

      {errorMessage && <ErrorMessage message={errorMessage} name={name} />}
    </div>
  );
};
